import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  Button,
  Stack,
  Input,
  FormControl,
  FormLabel,
  ModalClose,
  Typography,
  Box,
  Select,
  Option,
  Chip,
  SelectOption,
  ToggleButtonGroup,
  Tooltip
} from '@mui/joy';
import { getGlobalPositions, getEmployerPositions, getEmployer } from 'apiconfig/api';
import { useSession } from 'context/SessionContext';

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: { zipcode: string; roles: string[]; employers: string[]; employerPositions: string[] }) => void;
  currentZipcode: string;
  currentGlobalPositions: string[];
  currentEmployerFilterValue: string[];
  currentEmployerPositions?: string[];
  enableZipcodeFilter?: boolean;
  enableRolesFilter?: boolean;
  enableEmployerFilter?: boolean;
  enableEmployerPositionsFilter?: boolean;
}

const FilterModal: React.FC<FilterModalProps> = ({
  open,
  onClose,
  onApply,
  currentZipcode,
  currentGlobalPositions,
  currentEmployerFilterValue = ['other'],
  currentEmployerPositions = [],
  enableZipcodeFilter = true,
  enableRolesFilter = true,
  enableEmployerFilter = true,
  enableEmployerPositionsFilter = false
}) => {
  const [zipcode, setZipcode] = useState(currentZipcode);
  const [positions, setPositions] = useState<string[]>(currentGlobalPositions);
  const [employerPositions, setEmployerPositions] = useState<string[]>(currentEmployerPositions);
  const [employerFilterValue, setEmployerFilterValue] = useState<string[]>(currentEmployerFilterValue);
  const [globalPositions, setGlobalPositions] = useState<{id: string, displayName: string, slug: string}[]>([]);
  const [employerPositionsList, setEmployerPositionsList] = useState<any[]>([]);
  const [currentEmployer, setCurrentEmployer] = useState<{companyName: string} | null>(null);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingEmployerPositions, setLoadingEmployerPositions] = useState(false);
  const [loadingEmployer, setLoadingEmployer] = useState(false);
  const { sessionEmployerId } = useSession();

  // Fetch global positions (eager-loaded)
  useEffect(() => {
    const fetchGlobalPositions = async () => {
      try {
        setLoadingPositions(true);
        const response = await getGlobalPositions();
        setGlobalPositions(response.data?.globalPositions || []);
      } catch (err) {
        console.error("Error fetching global positions:", err);
      } finally {
        setLoadingPositions(false);
      }
    };

    if (enableRolesFilter && globalPositions.length === 0) {
      fetchGlobalPositions();
    }
  }, [enableRolesFilter, globalPositions.length]);

  // Fetch employer data for employer filter (eager-loaded)
  useEffect(() => {
    const fetchEmployerInfo = async () => {
      if (!sessionEmployerId) return;
      
      try {
        setLoadingEmployer(true);
        const employerResponse = await getEmployer(sessionEmployerId);
        setCurrentEmployer(employerResponse.data.employer);
      } catch (err) {
        console.error("Error fetching employer info:", err);
      } finally {
        setLoadingEmployer(false);
      }
    };

    if (enableEmployerFilter && sessionEmployerId && !currentEmployer) {
      fetchEmployerInfo();
    }
  }, [enableEmployerFilter, sessionEmployerId, currentEmployer]);

  // Fetch employer positions and employer data (eager-loaded)
  useEffect(() => {
    const fetchEmployerData = async () => {
      if (!sessionEmployerId) return;
      
      try {
        setLoadingEmployerPositions(true);
        
        // Fetch employer info if not already fetched
        if (!currentEmployer) {
          const employerResponse = await getEmployer(sessionEmployerId);
          setCurrentEmployer(employerResponse.data.employer);
        }
        
        // Fetch employer positions
        const positionsResponse = await getEmployerPositions(sessionEmployerId, {
          sort_by: 'displayNameOverride',
          sort_direction: 'ASC'
        }, true);
        setEmployerPositionsList(positionsResponse.data.employerPositions || []);
        
      } catch (err) {
        console.error("Error fetching employer data:", err);
      } finally {
        setLoadingEmployerPositions(false);
      }
    };

    if (enableEmployerPositionsFilter && sessionEmployerId && employerPositionsList.length === 0) {
      fetchEmployerData();
    }
  }, [enableEmployerPositionsFilter, sessionEmployerId, employerPositionsList.length, currentEmployer]);

  useEffect(() => {
    setZipcode(currentZipcode);
    setPositions(currentGlobalPositions ? currentGlobalPositions : []);
    setEmployerPositions(currentEmployerPositions ? currentEmployerPositions : []);
  }, [currentZipcode, currentGlobalPositions, currentEmployerPositions, open]);

  const handleApply = () => {
    onApply({ zipcode, roles: positions, employers: employerFilterValue, employerPositions });
    onClose();
  };

  const handleClear = () => {
    onApply({ zipcode: '', roles: [], employers: [], employerPositions: [] });
    onClose();
  };

  const renderGlobalPositionValue = (selected: SelectOption<string>[]) => {
    return (
    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {Array.isArray(selected) && selected.map((value) => {
        // Look up display name from globalPositions for global roles
        const globalPosition = globalPositions.find(pos => pos.id === String(value));
        if (globalPosition) {
          return (
            <Chip key={String(value)} size="lg" color="primary">
              {globalPosition.displayName}
            </Chip>
          );
        }
        
        // Look up display name from employerPositionsList for employer positions
        const employerPosition = employerPositionsList.find(pos => pos.id === String(value));
        if (employerPosition) {
          return (
            <Chip key={String(value)} size="lg" color="primary">
              {employerPosition.displayNameOverride || employerPosition.position?.displayName}
            </Chip>
          );
        }
        
        // Fallback to the value itself if not found
        return (
          <Chip key={String(value)} size="lg" color="primary">
            {String(value.label || value)}
          </Chip>
        );
      })}
    </Box>
  )}

  const inputStyles = {
    height: '56px',
    backgroundColor: '#ECEFF3',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.125rem',
    color: '#1A1B1D',
    '--Input-focusedHighlight': 'transparent !important',
    '&::placeholder': {
      color: '#7B7F85 !important',
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: '#ECEFF3',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: '#ECEFF3',
    },
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        size="md"
        sx={{
          backgroundColor: 'white',
          maxWidth: 500,
          width: { xs: '95vw', sm: '90vw' },
          maxHeight: '90vh',
          overflow: 'auto',
          mx: { xs: 1, sm: 0 },
          p: { xs: 3, sm: 4 }
        }}
      >
        <ModalClose />
        <Box sx={{ mb: 3 }}>
          <Typography level="h3" sx={{ fontWeight: 700 }}>
            Filter Candidates
          </Typography>
        </Box>

        <Stack spacing={3}>
          {enableEmployerFilter && (
            <FormControl sx={{ marginBottom: '24px' }}>
              <ToggleButtonGroup
                value={employerFilterValue}
                onChange={(event, newValue) => {
                  setEmployerFilterValue(newValue);
                }}
                disabled={loadingEmployer}
                sx={{
                  width: '100%',
                  '& .MuiButton-root': {
                    flex: 1,
                    maxWidth: '50%',
                    px: 2,
                    bgcolor: '#FFFFFF',
                    color: '#1A1B1D',
                    border: '1px solid #D1D5DB',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: '#F9FAFB',
                      borderColor: '#9CA3AF',
                    },
                    '&[aria-pressed="true"]': {
                      bgcolor: '#2E2D7E',
                      color: '#FFFFFF',
                      borderColor: '#2E2D7E',
                      '&:hover': {
                        bgcolor: '#1E1D6E',
                        borderColor: '#1E1D6E',
                      },
                    },
                  },
                }}
              >
                <Tooltip title={loadingEmployer ? 'Loading...' : (currentEmployer?.companyName || 'Current Employer')} arrow>
                  <Button 
                    value={sessionEmployerId || ''} 
                    sx={{ 
                      borderTopRightRadius: 0, 
                      borderBottomRightRadius: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {loadingEmployer ? 'Loading...' : (currentEmployer?.companyName || 'Current Employer')}
                  </Button>
                </Tooltip>
                <Tooltip title="All Other" arrow>
                  <Button 
                    value="other" 
                    sx={{ 
                      borderTopLeftRadius: 0, 
                      borderBottomLeftRadius: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    All Other
                  </Button>
                </Tooltip>
              </ToggleButtonGroup>
            </FormControl>
          )}

          {enableRolesFilter && (
            <FormControl sx={{ marginBottom: '24px' }}>
              <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                Role
              </FormLabel>
              <Select
                multiple
                value={positions}
                renderValue={renderGlobalPositionValue}
                onChange={(_, newValue) => {
                  if (Array.isArray(newValue)) {
                    setPositions(newValue);
                  }
                }}
                placeholder={loadingPositions ? "Loading positions..." : "Select positions"}
                disabled={loadingPositions}
                sx={{
                  ...inputStyles,
                  minHeight: '56px',
                  height: 'auto',
                }}
              >
                {globalPositions.map((position) => (
                  <Option key={position.id} value={position.id}>
                    {position.displayName}
                  </Option>
                ))}
              </Select>
            </FormControl>
          )}

          {enableEmployerPositionsFilter && (
            <FormControl sx={{ marginBottom: '24px' }}>
              <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                Applied to position at {currentEmployer?.companyName || 'this company'}
              </FormLabel>
              <Select
                multiple
                value={employerPositions}
                renderValue={renderGlobalPositionValue}
                onChange={(_, newValue) => {
                  if (Array.isArray(newValue)) {
                    setEmployerPositions(newValue);
                  }
                }}
                placeholder={loadingEmployerPositions ? "Loading positions..." : "Select positions"}
                disabled={loadingEmployerPositions}
                sx={{
                  ...inputStyles,
                  minHeight: '56px',
                  height: 'auto',
                }}
              >
                {employerPositionsList.map((position) => (
                  <Option key={position.id} value={position.id}>
                    {position.displayNameOverride || position.position?.displayName}
                  </Option>
                ))}
              </Select>
            </FormControl>
          )}

          {enableZipcodeFilter && (
            <FormControl sx={{ marginBottom: '24px' }}>
              <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
                Zip Code
              </FormLabel>
              <Input
                placeholder="Enter zip code"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)}
                inputMode="numeric"
                autoComplete="postal-code"
                sx={inputStyles}
              />
            </FormControl>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 4, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleClear}
              sx={{
                height: '48px',
                fontSize: '16px',
                fontWeight: 600,
                borderColor: '#7B7F85',
                color: '#7B7F85',
                '&:hover': {
                  borderColor: '#1A1B1D',
                  color: '#1A1B1D',
                },
              }}
            >
              Clear Filters
            </Button>
            <Button
              variant="solid"
              onClick={handleApply}
              sx={{
                height: '48px',
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: '#2e2d7e',
                '&:hover': {
                  backgroundColor: '#1f1c5c',
                },
              }}
            >
              Apply Filters
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default FilterModal; 